import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaClipboardList, FaPlus, FaSearch, FaExternalLinkAlt, FaTrash } from "react-icons/fa";

import Pagination from "Components/Pagination";
import CommonModal from "Components/CommonModal";
import Modal from "Components/UI/Modal";
import WorkMemoCreateModal from "Components/WorkMemo/WorkMemoCreateModal";
import { commonModalSetting } from "JS/common";
import { getWorkMemoTimeline, updateWorkMemoStatus, deleteWorkMemoEntry } from "JS/API";

import { commonStyles, designTokens, badgeStyles, listPageStyles } from "utils/designSystem";
import { renderBold } from "utils/markdown";
import { TableCell, TableContainer, TableHeader, Table } from "Components/UI/Table";
import PageContainer from "Components/UI/PageContainer";
import Select from "Components/UI/Select";
import Button from "Components/UI/Button";

// 채널 코드 정의 (API 문서에 맞춰 소문자 사용)
const CHANNEL_OPTIONS = [
  { value: '', label: '전체 채널' },
  { value: 'markpick', label: '마크픽' },
  { value: 'kmong', label: '크몽' },
  { value: 'phone', label: '전화' },
  { value: 'email', label: '메일' },
  { value: 'kakao', label: '카톡' },
  { value: 'kakao_channel', label: '카톡채널' },
  { value: 'ceo_order', label: '대표님 지시' },
  { value: 'etc', label: '기타' },
];

// 상태 코드 정의 (API 문서에 맞춰 snake_case 사용)
const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'in_progress', label: '진행중' },
  { value: 'completed', label: '완료' },
  { value: 'on_hold', label: '보류' },
];

// 채널별 배지 색상
const getChannelBadgeStyle = (channel) => {
  const styles = {
    markpick: { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    kmong: { backgroundColor: '#fef3c7', color: '#d97706' },
    phone: { backgroundColor: '#dcfce7', color: '#16a34a' },
    email: { backgroundColor: '#f3e8ff', color: '#9333ea' },
    kakao: { backgroundColor: '#fef9c3', color: '#ca8a04' },
    kakao_channel: { backgroundColor: '#fde68a', color: '#92400e' },
    ceo_order: { backgroundColor: '#fee2e2', color: '#dc2626' },
    etc: { backgroundColor: '#f1f5f9', color: '#475569' },
  };
  return styles[channel] || { backgroundColor: '#f1f5f9', color: '#475569' };
};

// 상태별 배지 색상
const getStatusBadgeStyle = (status) => {
  const styles = {
    in_progress: { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    completed: { backgroundColor: '#dcfce7', color: '#16a34a' },
    on_hold: { backgroundColor: '#f1f5f9', color: '#475569' },
  };
  return styles[status] || styles.in_progress;
};

// 채널 한글명
const getChannelLabel = (channel) => {
  const labels = {
    markpick: '마크픽',
    kmong: '크몽',
    phone: '전화',
    email: '메일',
    kakao: '카톡',
    kakao_channel: '카톡채널',
    ceo_order: '대표님 지시',
    etc: '기타',
  };
  return labels[channel] || channel;
};

// 상태 한글명
const getStatusLabel = (status) => {
  const labels = {
    in_progress: '진행중',
    completed: '완료',
    on_hold: '보류',
  };
  return labels[status] || status;
};

// 타임라인 타입별 정보
const getTimelineTypeInfo = (type) => {
  const types = {
    memo: { label: '메모', icon: '📝', color: '#3b82f6' },
    markpick_agent: { label: '출원대행', icon: '📋', color: '#059669' },
    markpick_application: { label: '출원정보', icon: '📄', color: '#8b5cf6' },
    markpick_comment: { label: '코멘트', icon: '💬', color: '#ec4899' },
    markpick_payment: { label: '결제', icon: '💳', color: '#06b6d4' },
    markpick_add_payment: { label: '추가결제', icon: '💰', color: '#f97316' },
  };
  return types[type] || { label: type, icon: '📌', color: '#64748b' };
};


const WorkMemo = () => {
  const navigate = useNavigate();
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 20,
  });

  // 필터 상태
  const [channelFilter, setChannelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [keyword, setKeyword] = useState('');

  // 데이터 상태
  const [memoList, setMemoList] = useState([]);
  const [loading, setLoading] = useState(false);

  // 모달 상태
  const [createModal, setCreateModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  // 스타일
  const pageStyles = {
    controlWrapper: {
      ...commonStyles.card,
      padding: designTokens.spacing.lg,
      marginBottom: designTokens.spacing.lg,
    },
    statusTabContainer: {
      display: 'flex',
      gap: designTokens.spacing.sm,
      marginBottom: designTokens.spacing.lg,
      paddingBottom: designTokens.spacing.lg,
      borderBottom: `1px solid ${designTokens.colors.borderLight}`,
    },
    statusTab: {
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.lg}`,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.border}`,
      backgroundColor: designTokens.colors.surface,
      color: designTokens.colors.text.secondary,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    statusTabActive: {
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.lg}`,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.primary}`,
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    controlBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: designTokens.spacing.md,
    },
    filterGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
      flexWrap: 'wrap',
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      backgroundColor: '#f8fafc',
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.md}`,
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      fontSize: '14px',
      width: '200px',
      color: designTokens.colors.text.primary,
    },
    noDataContainer: listPageStyles.noDataContainer,
    customerName: {
      fontWeight: '600',
      color: designTokens.colors.text.primary,
    },
    contactCell: {
      fontSize: '13px',
      color: designTokens.colors.text.secondary,
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs,
    },
    contentCell: {
      fontSize: '14px',
      color: designTokens.colors.text.primary,
      maxWidth: '350px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    dateCell: {
      fontSize: '13px',
      color: designTokens.colors.text.tertiary,
      whiteSpace: 'nowrap',
      display: 'inline-block',
    },
    createdBy: {
      fontSize: '12px',
      color: designTokens.colors.text.muted,
      marginTop: '2px',
    },
  };

  // 타임라인 목록 조회
  const getMemoList = async () => {
    setLoading(true);

    const result = await getWorkMemoTimeline(
      pageInfo.page,
      pageInfo.limit,
      channelFilter,
      statusFilter,
      keyword,
      null // itemType - 전체 조회
    );

    // 에러 처리
    if (typeof result === 'string') {
      setLoading(false);
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      commonModalSetting(setAlertBox, true, "alert", "목록을 불러오는데 실패했습니다.");
      return;
    }

    // API 응답 처리
    const { data, meta } = result?.data || {};
    setMemoList(data || []);
    setPageInfo(prev => ({
      ...prev,
      totalPage: meta?.total_page || 1,
    }));
    setLoading(false);
  };

  // 날짜 포맷 (월.일 시:분)
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${month}.${day < 10 ? '0' + day : day} ${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}`;
  };

  // 전화번호 포맷 (하이픈 추가)
  const formatPhone = (phone) => {
    if (!phone) return '-';
    const numbers = phone.replace(/[^0-9]/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    } else if (numbers.length === 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    return phone;
  };

  // 행 클릭 - 상세 모달 열기
  const handleRowClick = (item) => {
    setSelectedItem(item);
    setDetailModal(true);
  };

  // 상세 페이지로 이동
  const goToDetail = (item) => {
    setDetailModal(false);

    switch (item.type) {
      case 'memo':
        if (item.memo_id) {
          navigate(`/work-memo/${item.memo_id}?entry=${item.entry_id}`);
        }
        break;
      case 'markpick_agent':
        if (item.target_id) {
          navigate(`/agencies/${item.target_id}`);
        }
        break;
      case 'markpick_application':
        if (item.target_id) {
          navigate(`/application-info/${item.target_id}`);
        }
        break;
      case 'markpick_comment':
        // target_gubun으로 분기
        const gubun = item.target_gubun || item.targetGubun || '';
        const tid = item.target_id || item.targetId;
        if (gubun.toUpperCase() === 'AGENT' && tid) {
          navigate(`/agencies/${tid}`);
        } else if (gubun.toUpperCase() === 'APPLICATION' && tid) {
          navigate(`/application-info/${tid}`);
        }
        break;
      case 'markpick_add_payment':
        if (item.link) {
          navigate(item.link);
        }
        break;
      default:
        if (item.link) {
          navigate(item.link);
        }
        break;
    }
  };

  // 검색 핸들러
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      getMemoList();
    }
  };

  // 메모 상태 변경
  const handleStatusChange = async (memoId, newStatus) => {
    const result = await updateWorkMemoStatus(memoId, newStatus);

    if (typeof result === 'string') {
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      commonModalSetting(setAlertBox, true, "alert", "상태 변경에 실패했습니다.");
      return;
    }

    // 선택된 아이템 상태 업데이트
    setSelectedItem(prev => ({ ...prev, status: newStatus }));

    // 리스트 갱신
    getMemoList();

    commonModalSetting(setAlertBox, true, "alert", "상태가 변경되었습니다.");
  };

  // 메모 Entry 삭제
  const handleDeleteMemo = async () => {
    if (!selectedItem?.memo_id || !selectedItem?.entry_id) return;

    const result = await deleteWorkMemoEntry(selectedItem.memo_id, selectedItem.entry_id);

    if (typeof result === 'string') {
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      commonModalSetting(setAlertBox, true, "alert", "삭제에 실패했습니다.");
      return;
    }

    // 모달 닫기
    setDeleteConfirmModal(false);
    setDetailModal(false);
    setSelectedItem(null);

    // 리스트 갱신
    getMemoList();

    commonModalSetting(setAlertBox, true, "alert", "메모가 삭제되었습니다.");
  };

  // 테이블 렌더링
  const renderTableBody = () => {
    return memoList.map((item) => {
      const typeInfo = getTimelineTypeInfo(item.type);
      const isMemo = item.type === 'memo';

      return (
        <tr
          key={item.id}
          onClick={() => handleRowClick(item)}
          style={{ cursor: 'pointer' }}
        >
          {/* 타입 */}
          <TableCell>
            <span style={{
              ...badgeStyles.base,
              backgroundColor: `${typeInfo.color}20`,
              color: typeInfo.color,
            }}>
              {typeInfo.icon} {typeInfo.label}
            </span>
          </TableCell>

          {/* 채널 (메모인 경우만) */}
          <TableCell>
            {isMemo && item.channel ? (
              <span style={{
                ...badgeStyles.base,
                ...getChannelBadgeStyle(item.channel),
              }}>
                {getChannelLabel(item.channel)}
              </span>
            ) : (
              <span style={{ color: designTokens.colors.text.muted }}>-</span>
            )}
          </TableCell>

          {/* 고객명/회원명 */}
          <TableCell>
            <span style={pageStyles.customerName}>
              {item.customer_name || item.user_name || '-'}
            </span>
          </TableCell>

          {/* 연락처 */}
          <TableCell>
            <span style={{ fontSize: '13px', color: designTokens.colors.text.secondary }}>
              {formatPhone(item.contact || item.phone)}
            </span>
          </TableCell>

          {/* 내용 */}
          <TableCell>
            <div
              style={pageStyles.contentCell}
              title={item.content}
              dangerouslySetInnerHTML={{ __html: renderBold(item.content) }}
            />
          </TableCell>

          {/* 상태 (메모인 경우만) */}
          <TableCell>
            {isMemo && item.status ? (
              <span style={{
                ...badgeStyles.base,
                ...getStatusBadgeStyle(item.status),
              }}>
                {getStatusLabel(item.status)}
              </span>
            ) : (
              <span style={{ color: designTokens.colors.text.muted }}>-</span>
            )}
          </TableCell>

          {/* 날짜 */}
          <TableCell style={{ minWidth: '110px', overflow: 'visible' }}>
            <div style={pageStyles.dateCell}>
              {formatDateTime(item.event_at || item.created_at)}
            </div>
            <div style={pageStyles.createdBy}>
              {item.admin_name || ''}
            </div>
          </TableCell>

          {/* 바로가기 */}
          <TableCell>
            {(() => {
              let link = null;
              let label = '';

              switch (item.type) {
                case 'memo':
                  if (item.memo_id) {
                    link = `/work-memo/${item.memo_id}`;
                    label = '상세';
                  }
                  break;
                case 'markpick_agent':
                  if (item.target_id) {
                    link = `/agencies/${item.target_id}`;
                    label = '대행';
                  }
                  break;
                case 'markpick_application':
                  if (item.target_id) {
                    link = `/application-info/${item.target_id}`;
                    label = '출원';
                  }
                  break;
                case 'markpick_comment':
                  const gubun = item.target_gubun || item.targetGubun || '';
                  const targetId = item.target_id || item.targetId;
                  if ((gubun.toUpperCase() === 'AGENT') && targetId) {
                    link = `/agencies/${targetId}`;
                    label = '대행';
                  } else if ((gubun.toUpperCase() === 'APPLICATION') && targetId) {
                    link = `/application-info/${targetId}`;
                    label = '출원';
                  }
                  break;
                case 'markpick_add_payment':
                  if (item.link) {
                    link = item.link;
                    label = '결제';
                  }
                  break;
                default:
                  if (item.link) {
                    link = item.link;
                    label = '보기';
                  }
                  break;
              }

              return link ? (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(link);
                  }}
                  style={{
                    color: designTokens.colors.primary,
                    fontSize: '13px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {label}
                </span>
              ) : (
                <span style={{ color: designTokens.colors.text.muted }}>-</span>
              );
            })()}
          </TableCell>
        </tr>
      );
    });
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 업무 메모";
  }, []);

  useEffect(() => {
    getMemoList();
  }, [pageInfo.page, channelFilter, statusFilter]);

  return (
    <PageContainer
      title="업무 메모"
      icon={<FaClipboardList />}
    >
      {/* 컨트롤 바 */}
      <div style={pageStyles.controlWrapper}>
        {/* 상태 탭 버튼 */}
        <div style={pageStyles.statusTabContainer}>
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              style={statusFilter === opt.value ? pageStyles.statusTabActive : pageStyles.statusTab}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* 필터 및 검색 */}
        <div style={pageStyles.controlBar}>
          <div style={pageStyles.filterGroup}>
            <Select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              style={{ minWidth: '130px' }}
            >
              {CHANNEL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>

            <div style={pageStyles.searchContainer}>
              <FaSearch size={14} style={{ color: designTokens.colors.text.muted }} />
              <input
                type="text"
                placeholder="고객명, 연락처, 메모 검색..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={handleSearch}
                style={pageStyles.searchInput}
              />
            </div>
          </div>

          <Button
            variant="primary"
            icon={<FaPlus />}
            onClick={() => setCreateModal(true)}
          >
            새 메모
          </Button>
        </div>
      </div>

      {/* 테이블 또는 빈 상태 */}
      {memoList.length ? (
        <TableContainer>
          <Table style={{ minWidth: '1000px' }}>
            <thead>
              <tr>
                <TableHeader>타입</TableHeader>
                <TableHeader>채널</TableHeader>
                <TableHeader>고객명</TableHeader>
                <TableHeader>연락처</TableHeader>
                <TableHeader>내용</TableHeader>
                <TableHeader>상태</TableHeader>
                <TableHeader style={{ minWidth: '100px' }}>날짜</TableHeader>
                <TableHeader>바로가기</TableHeader>
              </tr>
            </thead>
            <tbody>{renderTableBody()}</tbody>
          </Table>
        </TableContainer>
      ) : (
        <div style={pageStyles.noDataContainer}>
          <FaClipboardList
            size={48}
            style={{
              color: designTokens.colors.text.muted,
              marginBottom: designTokens.spacing.md
            }}
          />
          <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: designTokens.spacing.xs }}>
            {loading ? '불러오는 중...' : '업무 메모가 없습니다'}
          </div>
          <div style={{ fontSize: '14px' }}>
            {!loading && '새로운 업무 메모를 작성해보세요.'}
          </div>
        </div>
      )}

      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {/* 메모 생성 모달 */}
      {createModal && (
        <WorkMemoCreateModal
          onClose={() => setCreateModal(false)}
          onSuccess={() => {
            setCreateModal(false);
            getMemoList();
            commonModalSetting(setAlertBox, true, "alert", "메모가 등록되었습니다.");
          }}
        />
      )}

      {/* 알림 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {}}
        />
      )}

      {/* 상세 보기 모달 */}
      {detailModal && selectedItem && (() => {
        const typeInfo = getTimelineTypeInfo(selectedItem.type);
        const isMemo = selectedItem.type === 'memo';

        // 타입별 링크 여부 및 버튼 텍스트 결정
        const getLinkInfo = () => {
          switch (selectedItem.type) {
            case 'memo':
              return { hasLink: !!selectedItem.memo_id, buttonText: '상세보기' };
            case 'markpick_agent':
              return { hasLink: !!selectedItem.target_id, buttonText: '출원대행 보기' };
            case 'markpick_application':
              return { hasLink: !!selectedItem.target_id, buttonText: '출원정보 보기' };
            case 'markpick_comment':
              const g = selectedItem.target_gubun || selectedItem.targetGubun || '';
              const tId = selectedItem.target_id || selectedItem.targetId;
              if (g.toUpperCase() === 'AGENT' && tId) {
                return { hasLink: true, buttonText: '출원대행 보기' };
              } else if (g.toUpperCase() === 'APPLICATION' && tId) {
                return { hasLink: true, buttonText: '출원정보 보기' };
              }
              return { hasLink: false, buttonText: '' };
            case 'markpick_add_payment':
              return { hasLink: !!selectedItem.link, buttonText: '바로가기' };
            default:
              return { hasLink: !!selectedItem.link, buttonText: '바로가기' };
          }
        };

        const linkInfo = getLinkInfo();

        return (
          <Modal
            title={`${typeInfo.icon} ${typeInfo.label}`}
            onClose={() => {
              setDetailModal(false);
              setSelectedItem(null);
            }}
            width="500px"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: designTokens.spacing.md }}>
              {/* 메타 정보 */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: designTokens.spacing.md,
                padding: designTokens.spacing.md,
                backgroundColor: '#f8fafc',
                borderRadius: designTokens.borderRadius.md,
                fontSize: '13px',
                color: designTokens.colors.text.secondary,
              }}>
                {isMemo && selectedItem.channel && (
                  <div>
                    <span style={{ color: designTokens.colors.text.muted }}>채널: </span>
                    <span style={{
                      ...badgeStyles.base,
                      ...getChannelBadgeStyle(selectedItem.channel),
                      marginLeft: '4px',
                    }}>
                      {getChannelLabel(selectedItem.channel)}
                    </span>
                  </div>
                )}
                <div>
                  <span style={{ color: designTokens.colors.text.muted }}>
                    {isMemo ? '고객명: ' : '회원: '}
                  </span>
                  <strong>{selectedItem.customer_name || selectedItem.user_name || '-'}</strong>
                </div>
                {isMemo && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: designTokens.colors.text.muted }}>상태: </span>
                    <select
                      value={selectedItem.status || 'in_progress'}
                      onChange={(e) => handleStatusChange(selectedItem.memo_id, e.target.value)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '13px',
                        border: `1px solid ${designTokens.colors.border}`,
                        borderRadius: designTokens.borderRadius.sm,
                        backgroundColor: designTokens.colors.surface,
                        cursor: 'pointer',
                        outline: 'none',
                      }}
                    >
                      <option value="in_progress">진행중</option>
                      <option value="completed">완료</option>
                      <option value="on_hold">보류</option>
                    </select>
                  </div>
                )}
              </div>

              {/* 날짜/작성자 정보 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: designTokens.colors.text.muted,
              }}>
                <span>{formatDateTime(selectedItem.event_at || selectedItem.created_at)}</span>
                {selectedItem.admin_name && <span>작성자: {selectedItem.admin_name}</span>}
              </div>

              {/* 내용 */}
              <div
                style={{
                  padding: designTokens.spacing.lg,
                  backgroundColor: designTokens.colors.surface,
                  border: `1px solid ${designTokens.colors.borderLight}`,
                  borderRadius: designTokens.borderRadius.md,
                  minHeight: '100px',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: designTokens.colors.text.primary,
                  whiteSpace: 'pre-wrap',
                }}
                dangerouslySetInnerHTML={{ __html: renderBold(selectedItem.content) }}
              />

              {/* 버튼 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: designTokens.spacing.sm,
              }}>
                {/* 왼쪽: 삭제 버튼 (메모만) */}
                <div>
                  {isMemo && selectedItem.memo_id && selectedItem.entry_id && (
                    <Button
                      variant="danger"
                      icon={<FaTrash size={12} />}
                      onClick={() => setDeleteConfirmModal(true)}
                    >
                      삭제
                    </Button>
                  )}
                </div>

                {/* 오른쪽: 닫기, 상세보기 */}
                <div style={{ display: 'flex', gap: designTokens.spacing.md }}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDetailModal(false);
                      setSelectedItem(null);
                    }}
                  >
                    닫기
                  </Button>
                  {linkInfo.hasLink && (
                    <Button
                      variant="primary"
                      icon={<FaExternalLinkAlt size={12} />}
                      onClick={() => goToDetail(selectedItem)}
                  >
                    {linkInfo.buttonText}
                  </Button>
                )}
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* 삭제 확인 모달 */}
      {deleteConfirmModal && (
        <Modal
          title="메모 삭제"
          onClose={() => setDeleteConfirmModal(false)}
          width="400px"
        >
          <div style={{ textAlign: 'center', padding: designTokens.spacing.lg }}>
            <FaTrash size={32} style={{ color: designTokens.colors.danger, marginBottom: designTokens.spacing.md }} />
            <p style={{ fontSize: '16px', marginBottom: designTokens.spacing.sm }}>
              이 메모를 삭제하시겠습니까?
            </p>
            <p style={{ fontSize: '13px', color: designTokens.colors.text.muted }}>
              삭제된 메모는 복구할 수 없습니다.
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: designTokens.spacing.md, marginTop: designTokens.spacing.md }}>
            <Button variant="secondary" onClick={() => setDeleteConfirmModal(false)}>
              취소
            </Button>
            <Button variant="danger" onClick={handleDeleteMemo}>
              삭제
            </Button>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
};

export default WorkMemo;
